import { useState } from 'react';
import { ArrowRight, Bot, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { aiAgents, agentCategories, aiAgentsFaqs } from '@/data/aiAgents';
import { TiltCard } from '@/components/ui/TiltCard';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const categoryColors: Record<string, string> = {
  "Healthcare": "from-blue-600 to-cyan-600",
  "Finance": "from-emerald-500 to-teal-500",
  "E-commerce": "from-orange-500 to-amber-500",
  "Logistics & Fleet": "from-indigo-500 to-blue-500",
  "Real Estate": "from-violet-500 to-purple-500",
  "Enterprise Ops": "from-slate-600 to-gray-600",
  "Legal": "from-rose-500 to-red-500",
  "HR": "from-fuchsia-500 to-pink-500",
  "Sales & Marketing": "from-amber-500 to-orange-500",
  "Travel": "from-teal-500 to-cyan-500",
};

export function AIAgentsSection() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [visibleCount, setVisibleCount] = useState(6);

  const filteredAgents = selectedCategory === "All"
    ? aiAgents
    : aiAgents.filter(agent => agent.category === selectedCategory);

  const displayedAgents = filteredAgents.slice(0, visibleCount);

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      <div className="absolute top-0 left-0 w-1/3 h-1/3 bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Bot className="w-5 h-5 text-primary" />
            <span className="text-primary font-semibold tracking-wider uppercase text-sm">
              AI Agent Solutions
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            AI Agent Products &amp; Accelerators
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            In-house AI agents we build and adapt for clients across healthcare, finance, e-commerce, logistics, and
            enterprise operations — production-ready starting points, not client deliverables.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {agentCategories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              size="sm"
              onClick={() => {
                setSelectedCategory(category);
                setVisibleCount(6);
              }}
              className="rounded-full"
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Agents Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {displayedAgents.map((agent) => {
            const gradientClass = categoryColors[agent.category] || "from-primary to-primary/80";

            return (
              <TiltCard
                key={agent.slug}
                className="group glass-card-hover rounded-xl overflow-hidden transition-all duration-300 h-full flex flex-col items-stretch relative border border-border/50 bg-card/50 backdrop-blur-sm"
              >
                <div className="aspect-video w-full overflow-hidden bg-secondary/50">
                  <img
                    src={agent.image}
                    alt={`${agent.name} dashboard preview`}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>

                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors text-lg">
                      {agent.name}
                    </h3>
                    <span className={`text-xs px-2 py-0.5 rounded-full bg-gradient-to-br ${gradientClass} text-white font-medium whitespace-nowrap flex-shrink-0`}>
                      {agent.category}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground mb-4 line-clamp-3">
                    {agent.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {agent.frameworks.map((fw, i) => (
                      <span key={i} className="text-[10px] px-2 py-1 rounded-md bg-primary/10 text-primary font-medium flex items-center gap-1">
                        <Sparkles className="w-2.5 h-2.5" />
                        {fw}
                      </span>
                    ))}
                    {agent.technologies.slice(0, 3).map((tech, i) => (
                      <span key={i} className="text-[10px] px-2 py-1 rounded-md bg-secondary text-secondary-foreground">
                        {tech}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto pt-4 border-t border-border/50">
                    <Link
                      to="/contact/"
                      className="w-full inline-flex items-center justify-center gap-2 text-sm px-4 py-2.5 rounded-lg font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md hover:shadow-lg group-hover:translate-y-[-2px]"
                    >
                      <span>Discuss This Agent</span>
                      <ArrowRight className="w-3.5 h-3.5 opacity-70" />
                    </Link>
                  </div>
                </div>
              </TiltCard>
            );
          })}
        </div>

        {/* Load More */}
        {visibleCount < filteredAgents.length && (
          <div className="text-center mt-12">
            <Button
              variant="outline"
              size="lg"
              onClick={() => setVisibleCount(prev => prev + 6)}
              className="rounded-full px-8"
            >
              Load More Agents
            </Button>
          </div>
        )}

        {/* FAQ */}
        <div className="max-w-3xl mx-auto mt-20">
          <h3 className="text-2xl font-bold text-foreground mb-6 text-center">
            AI Agent Development — Frequently Asked Questions
          </h3>
          <Accordion type="single" collapsible className="border-t border-border/50">
            {aiAgentsFaqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`ai-agent-faq-${index}`}
                className="border-b border-border/50 [&[data-state=closed]>[role=region]]:hidden"
              >
                <AccordionTrigger className="py-5 text-left text-base font-medium text-foreground hover:no-underline">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent forceMount className="pb-5 text-muted-foreground">
                  <p className="max-w-[65ch] leading-relaxed">{faq.answer}</p>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}
